import { Component, EventEmitter, Input, Output, signal, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { DropdownObjectType } from '../../shared/types';
import { DropdownStateService } from '../../services/dropdown-state.service';

@Component({
  selector: 'form-select-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, MatMenuModule],
  templateUrl: './form-select-dropdown.component.html',
  styleUrl: './form-select-dropdown.component.scss'
})
export class FormSelectDropdownComponent implements OnInit, OnDestroy {
  @Input() selectedValue: DropdownObjectType | null = null;
  @Input() contentList: DropdownObjectType[] = [];
  @Input() placeholder: string = 'Select';
  @Input() disabled: boolean = false;
  @Input() showSearch: boolean = true;
  @Input() dropdownId: string = '';
  @Output() onItemSelect = new EventEmitter<DropdownObjectType>();

  searchQuery = signal<string>('');

  constructor(private dropdownStateService: DropdownStateService) {}

  ngOnInit() {
    // Generate unique ID if not provided
    if (!this.dropdownId) {
      this.dropdownId = 'dropdown-' + Math.random().toString(36).substr(2, 9);
    }
  }

  ngOnDestroy() {
    // Close dropdown when component is destroyed
    if (this.isOpen) {
      this.dropdownStateService.closeDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close dropdown if clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.form-select-container')) {
      this.dropdownStateService.closeDropdown();
      this.searchQuery.set('');
    }
  }

  get isOpen(): boolean {
    return this.dropdownStateService.isDropdownOpen(this.dropdownId);
  }

  get filteredContentList(): DropdownObjectType[] {
    if (!this.searchQuery()) {
      return this.contentList;
    }
    return this.contentList.filter(item =>
      item.label.toLowerCase().includes(this.searchQuery().toLowerCase())
    );
  }

  get displayValue(): string {
    return this.selectedValue?.label || this.placeholder;
  }

  onButtonClick(event: MouseEvent) {
    if (!this.disabled) {
      event.stopPropagation();
      if (this.isOpen) {
        this.dropdownStateService.closeDropdown();
      } else {
        this.dropdownStateService.openDropdown(this.dropdownId);
      }
    }
  }

  onItemClick(item: DropdownObjectType, event: MouseEvent) {
    event.stopPropagation();

    // Prevent selection of unavailable items
    if (item.status === 'unavailable') {
      return;
    }

    this.selectedValue = item;
    this.onItemSelect.emit(item);
    this.dropdownStateService.closeDropdown();
    this.searchQuery.set('');
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchQuery.set(target.value);
  }

  onSearchClick(event: MouseEvent) {
    event.stopPropagation();
  }

  onOutsideClick() {
    this.dropdownStateService.closeDropdown();
    this.searchQuery.set('');
  }
}
